/*
 * Copyright (c) 2017 SGS Europe. All rights reserved.
 *
 * This program is the CONFIDENTIAL and PROPRIETARY property of 2017 SGS Europe.
 * Any unauthorised use, reproduction, or transfer of this program is strictly
 * prohibited.
 *
 * File Name:	Approvals.js
 *
 * Description:	Encapsulates the code for the approvals logic.
 * 
 */

var Approval = (function () {

	"use strict";

	/**
	* Holds the approval that is currently loaded into the viewer.
	*/
	var approval = {};

	/**
	* Holds the constants declared against the object.
	*/
	var constants = {};

	/**
	* The original read only from the server.
	* This will hide/show the tools, but there is plenty of server side validation if they try to bypass this.
	*/
	var g_bReadOnly = true;

	/**
	* This will hide any approval status information.
	*/
	var g_bApprovalInformationHidden = true;

	/**
	 * Set when doc change event called and the document has not been changed.
	 * May not have full coverage as it was used for a specific purpose.
	 */
	var g_bDocNotChanged = false;

	/**
	* Object to subscribe and publish custom events
	*/

	var g_oEvents = {};

	/**
	 * Get the parameters and query the server for a annotation report.
	 * @param {bool} bUseCompareApproval - Whether to use comparison approvals.
	 * @param {bool} bShowPreviousApprovals - Whether to show previous approvals.
	 * @param {array} liDocuments - Documents to make the report from.
	 */
	function createAnnotationsReport(bUseCompareApproval, bShowPreviousApprovals, liDocuments) {

		// Lock the screen.
		lockScreen();

		// Get the information for the current approval.
		var iApprovalId = (approval == null ? 0 : approval.approvalId);
		var iDateTimeZone = (new Date).getTimezoneOffset();

		// If we are using the compare approval, use the information for the compare approval.
		if (bUseCompareApproval) {
			for (var i = 0; i < liDocuments.length; i++) {
				if (liDocuments[i].greenlightDocumentId == g_oCompare.g_oCompareDocument.greenlightDocumentId) {
					iApprovalId = liDocuments[i].greenlightApprovalId;
					break;
				}
			}
		}

		// If a user wants all approvals we need to loop around the comparable documents from the
		// viewer and add the IDs which can then be used to get the PDF.
		var oApprovalIds = [];
		oApprovalIds.push(0);
		if (bShowPreviousApprovals) {
			for (var i = 0; i < liDocuments.length; i++) {
				// Double check the document id is not the same as the current approval, isn't 0 and that the approval id is smaller than the current approval.
				// This is to stop future versions of a report appearing on the proof report.
				if ((liDocuments[i].greenlightApprovalId != iApprovalId) && liDocuments[i].greenlightApprovalId > 0 && (liDocuments[i].greenlightApprovalId < iApprovalId)) {
					oApprovalIds.push(liDocuments[i].greenlightApprovalId);
				}
			}
		}

		// Create Server Call for the data.
		var oJSON = {
			primaryApprovalId: iApprovalId,
			secondaryApprovalIds: oApprovalIds,
			timeZone: iDateTimeZone,
			dateFormat: g_oSettings.c_szDateFormat,
			hideApprovalInformation: g_oApproval.g_bApprovalInformationHidden
		};

		// Call the server to create the annotations report.
		$.post({
			url: "GreenlightApproval/createAnnotationsReport",
			data: JSON.stringify(oJSON),
			contentType: "application/json; charset=utf-8",
			dataType: "json",
			cache: false,
			success: function (oResponse) {
				if (oResponse.Success) {
					window.open(JSON.parse(oResponse.Contents), "_blank");
				}
				else {
					g_oGreenlightMessages.displayErrorBubble(displayErrorContentHTML(oResponse.Errors));
				}
			},
			error: function () {
				g_oGreenlightMessages.displayErrorBubble(g_oAnnotations.c_szPrintAnnotationsFailed);
			}
		})
		.always(function () {
			unlockScreen();
		});
	}

	/**
	 * Called when the document has been changed.
	 * @param {Document Object} oDoc- The value of the selected document.
	 */
	function docChangeEvent(oDoc) {
		
		// If we have changed the document reset the manual alignment to auto-align trim boxes
		if (!g_oApproval.g_bDocNotChanged) {
			g_oCompare.g_bManualAlignmentSet = false;
		}

		// Reset flags.
		g_oViewer.g_bReadOnly = false;
		g_oApproval.g_bDocNotChanged = false;
		g_oTextCompare.g_bDifferenceAnnotations = false;

		// Ensure we have a document.
		if ($.isEmptyObject(oDoc) || oDoc === null) {
			var oComboControl = comboControl_GetControlById("approvalNameComboControl", false);
			oDoc = oComboControl.documents[oComboControl.selectedDocumentIndex];
		}
		
		// If this document has an approval id, and is not archived.
		if (oDoc.greenlightApprovalId > 0 && oDoc.document.documentStatus != g_oArchive.g_ArchivedDocumentStatus) {

			// Get the approval for the selected document.
			getApproval(oDoc.greenlightApprovalId, oDoc.greenlightDocumentId);
		}
		// If this document does not have an approval id.
		else {

			// Reset the list of available compare types.
			g_oCompare.setAvailableCompareModes(eCompareMode.NoCompare, oDoc.document.documentType, oDoc.document.documentType);

			// Set the approval.
			setApproval(null);
		}
	}

	/**
	 * Gets the approval from the database.
	 * @param {int} approvalId - Id of the approval we are getting.
	 * @param {int} documentId - Id of the document relating to the approval.
	 */
	function getApproval(approvalId, documentId) {
		var oJSON = {
			SessionId: g_szSessionId,
			ApprovalId: approvalId,
			DocumentId: documentId
		};
		
		$.post({
			url: "GreenlightApproval/getApproval",
			data: JSON.stringify(oJSON),
			contentType: "application/json; charset=utf-8",
			dataType: "json",
			cache: false,
			success: function (oResponse, Status, jqXHR) {
				if (oResponse.Success && oResponse.Errors.length === 0) {
					// Reset ReadOnly flag.
					g_oViewer.g_bReadOnly = false;
					setApproval(JSON.parse(oResponse.Contents));
				} else {
					g_oGreenlightMessages.displayErrorBubble(displayErrorContentHTML(oResponse.Errors));
				}
			}
		});
	}

	/**
	 * Gets the approval details for this user.
	 * @returns {object} - the approval user details.
	 */
	function getApprovalUser() {

		// Create the approver to be returned.
		var oApprover = null;

		// Extract the approvers.
		var oaApprovers = approval.approvers;

		// Get the user.
		for (var i = 0; i < oaApprovers.length; i++) {
			if (oaApprovers[i].userId === g_oUserDetails.UserId && oaApprovers[i].thirdPartyApproverId === g_oUserDetails.ThirdParyMemberId) {
				oApprover = oaApprovers[i];
				break;
			}
		}

		// If there not on the approval create a dummy account.
		if (oApprover === null) {
			oApprover = {
				approverStatus: eApproverStatus.Pending,
				approverStatusDateTime: "",
				thirdPartyApproverId: null
			};
		}

		// Return the result.
		return oApprover;
	}

	/**
	 * Determines if the approval is read only.
	 * @returns {bool} - Determines if the approval should be set read only.
	 */
	function isReadOnly() {
		var bReadOnly = false;

		// If we have set the approval as read only from the server.
		// Or we have set the view as read only from the multi view logic.
		// Then it is always going to be read only.
		if (g_oViewer.g_bReadOnly || (typeof (g_oMultiView) != "undefined" && g_oMultiView.bMultiViewReadOnly)) {
			bReadOnly = true;
		}
		else {

			// If there is no approval then it is read only.
			if (approval == null) {
				bReadOnly = true;
			}
			else {
				// If the approval is not pending then it is read only.
				if (approval.approvalStatus !== eApproverStatus.Pending) {
					bReadOnly = true;
				}
				else {

					var oUser = getApprovalUser();

					if (oUser === null || oUser.thirdPartyApproverId === null || oUser.approverStatus !== eApproverStatus.Pending) {

						bReadOnly = true;
					}
				}
			}
		}

		return bReadOnly;
	}

	/**
	 * The on click action for the approve icon.
	 * @param {event} event - Reference to the onclick event.
	 */
	function onClickApprove(event) {

		// Grab the parent div.
		var thisParent = $(event).parent();

		// Highlight the button.
		highlightSelectedButton(thisParent, true);

		// Update the approval button states.
		updateApprovalButtonStates();

		// If the dropdown is not open.
		if (!thisParent.find(".dropdown-content").is(":visible")) {

			// Toggle the dropdown.
			$("#divApprovalDropDown").slideToggle(200);
		}
		// If the dropdown is already open.
		else {

			// Clear the highlight on the button.
			clearHighlightSelectedButton(thisParent);
		}
	}

	/**
	 * The on click action for the reject icon.
	 * @param {event} event - Reference to the onclick event.
	 */
	function onClickReject(event) {

		// Grab the parent div.
		var thisParent = $(event).parent();

		// Highlight the button.
		highlightSelectedButton(thisParent, true);

		// Update the approval button states.
		updateApprovalButtonStates();

		// If the dropdown is not open.
		if (!thisParent.find(".dropdown-content").is(":visible")) {

			// Toggle the dropdown.
			$("#divRejectionDropDown").slideToggle(200);
		}
		// If the dropdown is already open.
		else {

			// Clear the highlight on the button.
			clearHighlightSelectedButton(thisParent);
		}
	}

	/**
	 * Updates the approval button states.
	 */
	function updateApprovalButtonStates() {

		// Update the button states.
		g_oApproval.ApprovalConditionsMet(false) ? $("#btnConfirmApprove").removeClass("approveRejectButton--disabled") : $("#btnConfirmApprove").addClass("approveRejectButton--disabled");
		g_oApproval.ApprovalConditionsMet(false) ? $("#btnConfirmConditionalApprove").removeClass("approveRejectButton--disabled") : $("#btnConfirmConditionalApprove").addClass("approveRejectButton--disabled");
		g_oApproval.RejectionConditionsMet(false) ? $("#btnConfirmReject").removeClass("approveRejectButton--disabled") : $("#btnConfirmReject").addClass("approveRejectButton--disabled");
	}

	/**
	 * Determines if the approval conditions have been met.
	 * @param {bool} displayWarnings - If warnings should be displaid.
	 */
	function approvalConditionsMet(displayWarnings) {

		// Get the current approval user.
		var user = getApprovalUser();

		// Validate the users checklist.
		// If the user has a checklist and it is not in the correct state. (3D file checklists are not yet supported)
		if (user.hasChecklist && approval.document.documentType != eDocumentTypes.ThreeD && g_oChecklist.oChecklist.ChecklistState != g_oChecklist.g_eChecklistItemState.Pass) {

			// If we are displaying warnings.
			if (displayWarnings) {

				// Make the checklist visible.
				g_oChecklist.makeChecklistTabVisible();

				// Show that the checklist is incomplete.
				g_oGreenlightMessages.displayWarningBubble(c_szIncompleteChecklistWarning);
			}

			// Approval conditions not met.
			return false;
		}

		// Approval conditions have been met.
		return true;
	}

	/**
	 * Determines if the rejection conditions have been made.
	 * @param {bool} displayWarnings - If warnings should be displaid.
	 */
	function rejectionConditionsMet(displayWarnings) {

		// Get the documents for the approval.
		var oApprovalFiles = g_oApprovalFiles.g_oControlIndex[comboControl_GetControlById("approvalNameComboControl", true)];

		// If the current approval has more than one document, we may have annotations on other documents, so we allow them to try and reject.
		if (oApprovalFiles.g_Docs.length > 1) {
			return true;
		}

		// In a single document approval, we need at least one annotation.
		if (!g_oAnnotations.checkAnnotationsExistOnDocument()) {

			// If we are displaying warnings, show we are missing annotations.
			if (displayWarnings) {
				g_oGreenlightMessages.displayWarningBubble(c_szMissingAnnotationsWarning);
			}

			// Rejection conditions not met.
			return false;
		}

		// Rejection conditions met.
		return true;
	}

	/**
	 * Registers the approvals events.
	 * @param {object} oEventService - The event service we are registering the events to.
	 */
	function registerEvents(oEventService) {
		g_oEvents = oEventService;
		g_oEvents.subscribe("changeDoc", docChangeEvent);
	}

	/**
	 * Renders the approval bar based upon the approval.
	 */
	function renderApprovalBar() {

		// Reset the approval status bar.
		var oBar = $("#divApprovalStatusBar");
		oBar.attr('class', 'approvalStatusBar');

		var szClass = "";

		// If we have an approval and the viewer is not open in read only.
		if ((approval != null || !isReadOnly()) && !g_oApproval.g_bApprovalInformationHidden) {

			// Determine the approval bar status to be used.
			switch (approval.approvalStatus) {
				case eApproverStatus.Approved:
					szClass = "approvalStatusBarApproved";
					break;
				case eApproverStatus.ApprovedChanges:
					szClass = "approvalStatusBarApprovedWithChanges";
					break;
				case eApproverStatus.Pending:
					szClass = "approvalStatusBarPending";
					break;
				case eApproverStatus.Rejected:
					szClass = "approvalStatusBarRejected";
					break;
			}
		}
		// Otherwise default to the pending status bar.
		else {
			szClass = "approvalStatusBarPending";
		}

		// Apply the class to the status bar.
		oBar.addClass(szClass);
	}

	/**
	 * Renders the correct approval icons.
	 */
	function renderApprovalIcons() {
		
		// Nuke any existing icons (or highlights).
		if (!g_oApproval.c_bApproveWithCompare || isReadOnly()) {
			$(".ApprovalIcon").hide();
		}

		// Reset options.
		$(".selectedButton").removeClass("selectedButton");
		$(".dropdown-content").hide();
		$("#tblBatchApprovalTools").hide();
		$("#btnPendingApproval").hide();

		// Show no icons if we have no approval or we are in a compare / text compare.
		if (approval === null || (g_oViewer.g_oCompareMode != undefined && g_oViewer.g_oCompareMode != eCompareMode.NoCompare && !g_oTextCompare.g_bTextCompareInProgress)) {
			return;
		}

		// Get the approval user.
		var oApprover = getApprovalUser();

		// If the user is a Sheriff on the current approval, hide the conditional approve icon.
		if (oApprover.isSheriff) {
			$("#divConfirmConditionalApproval").hide();
		}

		// Get the approval and read only status.
		var oApproval = approval;
		var bReadOnly = isReadOnly();

		// If we are in text compare, and we are not in read only, set that we can leave difference annotations on the text compare.
		if (g_oTextCompare.g_bTextCompareInProgress) {
			if (!bReadOnly) {
				g_oTextCompare.g_bDifferenceAnnotations = true;
			}
			return;
		}

		// If the viewer is read-only, remove the clickable icons.
		if (bReadOnly && !g_oApproval.g_bApprovalInformationHidden) {
			var szDate = "";
			if (oApproval.approvalStatus != eApproverStatus.Pending) {
				szDate = convertUTCDateToLocal(oApproval.approvalStatusDateTime);
			}

			switch (oApproval.approvalStatus) {
				case eApproverStatus.Approved:
					$("#btnApprove").show();
					if ($("#btnApprove").hasClass("tooltipstered")) {
						// The control has already been hovered over by the mouse and Tooltipster has created the custom tooltip and removed the 'title' attribute.
						$("#btnApprove").tooltipster('content', szDate);
					}
					else {
						$("#btnApprove").attr("title", szDate);
					}
					break;
				case eApproverStatus.ApprovedChanges:
					$("#btnConditionallyApproved").show();
					if ($("#btnConditionallyApproved").hasClass("tooltipstered")) {
						// The control has already been hovered over by the mouse and Tooltipster has created the custom tooltip and removed the 'title' attribute.
						$("#btnConditionallyApproved").tooltipster('content', szDate);
					}
					else {
						$("#btnConditionallyApproved").attr("title", szDate);
					}
					break;
				case eApproverStatus.Pending:
					$("#btnPendingApproval").show();
					break;
				case eApproverStatus.Rejected:
					$("#btnReject").show();
					if ($("#btnReject").hasClass("tooltipstered")) {
						// The control has already been hovered over by the mouse and Tooltipster has created the custom tooltip and removed the 'title' attribute.
						$("#btnReject").tooltipster('content', szDate);
					}
					else {
						$("#btnReject").attr("title", szDate);
					}
					break;
			}

			// Check the user has requested for the approval menu to vanish
			if (!g_oApproval.c_bApproveWithCompare) {
				// Remove the drop downs if they are visible.
				if ($("#divRejectionDropDown").is(":visible")) {
					$("#divRejectionDropDown").hide();
				}
				if ($("#divApprovalDropDown").is(":visible")) {
					$("#divApprovalDropDown").hide();
				}
			}
		}
		else if (!bReadOnly && !g_oApproval.g_bApprovalInformationHidden) {
			$(".ApprovalIcon").off("click"); // Make sure the click Events do not exist before re-binding them.

			$("#tblBatchApprovalTools").show();

			if ($("#btnApprove").hasClass("tooltipstered")) {
				$("#btnApprove").tooltipster('content', g_oApproval.c_szApprovalIconApprove);
			}

			$("#btnApprove").show();
			$("#btnApprove").click(function () {
				if (!isReadOnly() && !g_oAnnotations.checkForUnsavedAnnotation()) {
					onClickApprove(this);
				}
			});

			if ($("#btnReject").hasClass("tooltipstered")) {
				$("#btnReject").tooltipster('content', g_oApproval.c_szApprovalIconReject);
			}

			$("#btnReject").show();
			$("#btnReject").click(function () {
				if (!isReadOnly() && !g_oAnnotations.checkForUnsavedAnnotation()) {
					onClickReject(this);
				}
			});
		}
	}

	/**
	 * Renders the Approvers in the info list.
	 */
	function renderApproverInfoList() {
		
		// Remove the existing content from the approver list.
		$("#tblApproverInfoList").remove();

		// If we have no approval, or we are we are in a compare mode and we do not want to approve with Compare
		if (approval === null || ((g_oViewer.g_oCompareMode != undefined && g_oViewer.g_oCompareMode != eCompareMode.NoCompare) && !g_oApproval.c_bApproveWithCompare)) {

			// Hide the approval tools.
			$("#tblInfoTools").hide();
		}
		// If we have an approval and we are not in a compare mode.
		else {

			// Show the approval tools when not hidden.
			if (!g_oApproval.g_bApprovalInformationHidden) {
				$("#tblInfoTools").show();
			}

			// Get a list of the approvers.
			var oApprovers = approval.approvers;

			// Create a table to hold the approvers list.
			var szHTML = "<table id='tblApproverInfoList' style='width:100%;'>";

			// Loop through each of the approvers.
			for (var i = 0; i < oApprovers.length; i++) {

				// If this approver is a hidden approver, do not render them.
				if (oApprovers[i].approverType == eApproverType.Hidden) {
					continue;
				}

				// Outline the required variables.
				var szClass = "";
				var szImage = "";
				var szStatusMouseOver = "";

				// Determine the approver class, image and mouseover based on the status.
				switch (oApprovers[i].approverStatus) {

					case eApproverStatus.Pending:
						szClass = "pending";

						// If this user is a group.
						if (oApprovers[i].approverType == eApproverType.Group) {
							szImage = "img/Approval-In-Progress-Group_48.png";
							szStatusMouseOver = g_oApproval.c_szGroupApprovalPending;
						}
						// Otherwise default to an individual.
						else {
							szImage = "img/Approval-In-Progress_48.png";
							szStatusMouseOver = g_oApproval.c_szApprovalPending;
						}
						break;

					case eApproverStatus.Approved:
						szClass = "pending";
						szImage = "img/Complete-Task_48.png";
						szStatusMouseOver = convertUTCDateToLocal(oApprovers[i].approverStatusDateTime);
						break;

					case eApproverStatus.ApprovedChanges:
						szClass = "pending";
						szImage = "img/Conditionally-Approved_48.png";
						szStatusMouseOver = convertUTCDateToLocal(oApprovers[i].approverStatusDateTime);
						break;

					case eApproverStatus.Rejected:
						szClass = "pending";
						szImage = "img/Reject-01_48.png";
						szStatusMouseOver = convertUTCDateToLocal(oApprovers[i].approverStatusDateTime);
						break;
				}

				// Add the approver row with the mouse over.
				szHTML += "<tr>";

				// Add the approvers display name with the status class.
				szHTML += "<td class='approverDiv " + szClass + "'>" + renderApproverDisplayName(oApprovers[i]) + "</td>";

				// Add the approvers HasChecklist badge if appropriate. 
				szHTML += "<td class='approverDiv approverDivIcon'>";
				if (oApprovers[i].hasChecklist) {
					szHTML += "<img id='" + oApprovers[i].approvalUserId + "-ChecklistIcon" + "' class='approversSheriffImage tooltip' src='img/checklist_icon_orange.svg' title='" + "Checklist Pending" + "' />";
				}
				szHTML += "</td>";

				// Add the approvers sheriff badge if appropriate.
				szHTML += "<td class='approverDiv approverDivIcon'>";
				if (oApprovers[i].isSheriff) {
					szHTML += "<img class='approversSheriffImage tooltip' src='img/Sheriff_16.png' title='" + constants.c_szSheriffPrompt + "' />";
				}
				szHTML += "</td>";


				// Add the approvers status image.
				szHTML += "<td class='approverDiv approverDivIcon'>";
				szHTML += "<img class='approverStatusImage tooltip' src='" + szImage + "' title='" + szStatusMouseOver + "' />";
				szHTML += "</td>";

				// Close the approver row.
				szHTML += "</tr>";
			}

			// Close the table holding the approvers list.
			szHTML += "</table>";

			// Apply the HTML to the page.
			$("#divApprovalMembers").append(szHTML);
		}
	}

	/**
	 * Renders the approver display name.
	 * @param {object} oApprover - The approver to be rendered.
	 * @returns {string} - The approver display name.
	 */
	function renderApproverDisplayName(oApprover) {

		// Outline the default name to be returned.
		var szDisplayName = oApprover.userDetails.EmailAddress;

		// If we have a display name override.
		if (oApprover.displayName != null && oApprover.displayName != "") {

			// Use the display name.
			szDisplayName = oApprover.displayName;
		}
		// If we have a role.
		else if (oApprover.role != null && oApprover.role != "") {

			// Add this to the display name.
			szDisplayName += " (" + oApprover.role + ")";
		}

		// Return the display name.
		return szDisplayName;
	}

	/**
	 * Sets the current approval object.
	 * @param {object} newApproval - The new approval object.
	 */
	function setApproval(newApproval) {
		// Save the new approval.
		approval = newApproval;

		// Ensure read only is flagged correctly.
		validateReadOnly(approval);

		// Render the icons to reflect the new approvals status.
		renderApprovalIcons();
		renderApproverInfoList();
		renderApprovalBar();
		renderChecklist();
		renderChecklistIconList();

		// Publish that the approval has been set.
		g_oEvents.publish("approvalSet", approval);
	}

	/**
	 * Renders the checklist depending on the approval user.
	 */
	function renderChecklist() {

		// If we have no approval, reset any checklist.
		if (approval == null) {
			g_oChecklist.oApprovalUser = null;
			g_oChecklist.removeChecklist();
			return;
		}

		// Get the current approval user.
		var user = getApprovalUser();

		// Initialise the checklist.
		g_oChecklist.initialiseChecklist(user);
	}

	/**
	 * Renders the checklist icon list.
	 * @param {object} approval - The approval.
	 */
	function renderChecklistIconList() {

		// If we have no approval, or checklist, do nothing.
		if (approval == null || !approverHasChecklistInApproval(approval)) {
			return;
		}

		// Render the checklist icon list.
		g_oChecklist.renderChecklistIconList(approval);
	}

	/**
	 * Checks if a user has a checklist in an approval.
	 * @param {object} approval - The approval.
	 */
	function approverHasChecklistInApproval(approval) {

		// Check for a checklist.
		for (var i = 0; i < approval.approvers.length; i++) {
			if (approval.approvers[i].hasChecklist) {
				return true;
			}
		}

		// No checklist found.
		return false;
	}

	/**
	 * Checks if a user is a sheriff for the an approval.
	 * @param {number} userId - the user Id.
	 * @param {object} approval - the approval object.
	 * @returns - boolean indicating whether user is a sheriff or not. 
	 */
	function userIsSheriffInApproval(userId, approval){
		var bReturn = false;

		// Guard clauses.
		if(!approval) return bReturn;
		if(!userId) return bReturn;

		// Find the matching user and return its isSheriff property.
		approval.approvers.forEach(function (user) {
			if(user.userId === userId && user.isSheriff){
				bReturn = true;
			}
		});

		return bReturn;
	}

	/**
	 * Checks to ensure if the users UI should be set to read only for an approval.
	 * @param {object} approval - The approval object containing the details to be validated.
	 */
	function validateReadOnly(approval) {
		
		// If we have an approval and the viewer is not set to read only.
		if (approval !== null && !g_oViewer.g_bReadOnly) {

			// If the viewed approval is not the one for the task... (Happens with compare)
			g_oViewer.g_bReadOnly = (approval.approvalId !== g_iDefaultApprovalId);

			// If the status isn't pending, it should always be flagged as read only.
			if (approval.approvalStatus !== eApproverStatus.Pending) {
				g_oViewer.g_bReadOnly = true;
			}

			// If this has been requested as a read only approval, it should always be flagged as read only
			if (g_oViewer.g_oReadOnlyApprovals != undefined && g_oViewer.g_oReadOnlyApprovals.indexOf(approval.approvalId) != -1) {
				g_oViewer.g_bReadOnly = true;
			}
		}
	}

	/**
	 * Sets the approvers status.
	 * @param {int} iStatus - The selected status value.
	 */
	function setApproverStatus(iStatus) {

		// Get the approval user.
		var oApprover = getApprovalUser();

		// If we have an approval user.
		if (oApprover) {

			// If this is the sheriff we may need to show a prompt.
			if (oApprover.isSheriff) {

				// Keep track of the sheriff warning prompt to be shown.
				var bShowSheriffWarning = false;
				var szPrompt = constants.c_szSheriffWarningPrompt;

				// Setup the confirmation/rejection actions.
				var oConfirmAction = function () { setApproverStatusServerCall(iStatus, oApprover.thirdPartyApproverId); };
				var oRejectionAction = function () { };

				// Get any outstanding users.
				var oOutstandingUsers = $.grep(
					approval.approvers,
					function (approver) {
						return approver.thirdPartyApproverId != g_oUserDetails.ThirdParyMemberId && approver.approverType != eApproverType.Hidden && approver.approverStatus == eApproverStatus.Pending;
					}
				);

				// If the sheriff has approved, check to see if there are any rejections to prompt them.
				var oRejectedUsers = [];
				if (iStatus == eApproverStatus.Approved) {
					oRejectedUsers = $.grep(
						approval.approvers,
						function (approver) {
							return approver.thirdPartyApproverId != g_oUserDetails.ThirdParyMemberId && (approver.approverStatus == eApproverStatus.ApprovedChanges || approver.approverStatus == eApproverStatus.Rejected);
						}
					);
				}

				// If the sheriff has approved, check to see if there are any outstanding text classification annotations.
				var oNonApprovedTextClassificationAnnotations = [];
				if (iStatus == eApproverStatus.Approved) {
					oNonApprovedTextClassificationAnnotations = $.grep(
						g_oAnnotations.g_oAnnotationsMade,
						function (annotation) {
							return annotation.g_iAnnotationType == eAnnotationTypes.TextClassification && $.grep(annotation.g_oStatuses, function (annotationStatus) { return annotationStatus.g_iAnnotationStatus == eAnnotationStatuses.Query || annotationStatus.g_iAnnotationStatus == eAnnotationStatuses.ToDo; }).length > 0;
						}
					);
				}

				// If there are rejected users, warn the sheriff.
				if (oRejectedUsers.length > 0) {
					szPrompt += "<br/><br/>" + constants.c_szSheriffWarningRejectedApprovers;
					bShowSheriffWarning = true;
				}

				// If there are outstanding users, warn the sheriff.
				if (oOutstandingUsers.length > 0) {
					szPrompt += "<br/><br/>" + constants.c_szSheriffWarningOutstandingApprovers;
					bShowSheriffWarning = true;
				}

				// If there are non-approved text classification annotations, warn the sheriff.
				if (oNonApprovedTextClassificationAnnotations.length > 0) {
					szPrompt += "<br/><br/>" + constants.c_szSheriffWarningUnapprovedTextClassifications;
					bShowSheriffWarning = true;
				}

				// If we require a warning.
				if (bShowSheriffWarning) {
					g_oGreenlightMessages.displayConfirmBubble(szPrompt, oConfirmAction, oRejectionAction);
				}
				// If we do not require a warning.
				else {
					oConfirmAction();
				}
			}
			// If this user is not the sheriff.
			else {
				// Set the approver status.
				setApproverStatusServerCall(iStatus, oApprover.thirdPartyApproverId);
			}
		}
	}

	/**
	 * Call the server to set the approver status.
	 * @param {int} iStatus - The approvers status.
	 * @param {string} szThirdPartyApproverId - the approvers third party approver id.
	 */
	function setApproverStatusServerCall(iStatus, szThirdPartyApproverId) {

		// Build the JSON to be sent.
		var oJSON = {
			SessionId: g_szSessionId,
			ThirdPartyMemberId: szThirdPartyApproverId,
			ApprovalId: approval.approvalId,
			UserId: g_oUserDetails.UserId,
			Status: parseInt(iStatus, 10)
		};

		// Set the approver status.
		$.post({
			url: "GreenlightApproval/setApproverStatus",
			data: JSON.stringify(oJSON),
			contentType: "application/json; charset=utf-8",
			dataType: "json",
			cache: false,
			success: function (oResponse) {

				if (oResponse.Success && oResponse.Errors.length === 0) {
					setApproverStatus_CH(iStatus);
				} else {
					g_oGreenlightMessages.displayErrorBubble(displayErrorContentHTML(oResponse.Errors));
				}
			}

		}).always(function () {
			$("#btnBatchReject").removeClass("selectedButton");
			$("#btnBatchApprove").removeClass("selectedButton");
		});
	}

	/**
	 * Calls GL's API to approve or reject every
	 * @param {int} iStatus - The Status value to be set.
	 * @param {bool} bBypassStatusWarning - The Bypass Status Warning flag to be set.
	 */
	function setBatchApproverStatus(iStatus, bBypassStatusWarning) {

		var oJSON = {
			SessionId: g_szSessionId,
			Status: iStatus,
			BypassStatusWarnings: bBypassStatusWarning
		};

		$.post({
			url: "GreenlightApproval/setBatchApproverStatus",
			data: JSON.stringify(oJSON),
			contentType: "application/json; charset=utf-8",
			dataType: "json",
			cache: false,
			success: function (oResponse) {
				if (oResponse.Success && oResponse.Errors.length === 0) {
					// If there is a warning, we need to display the notification to the user asking them to confirm.
					var oResponseObject = JSON.parse(oResponse.Contents);

					if (oResponseObject.Warning) {
						// Setup the confirmation/rejection actions.
						var oConfirmAction = function () {
							// Set the approver status.
							setBatchApproverStatus(iStatus, true);
						};
						var oRejectionAction = function () { };

						g_oGreenlightMessages.displayConfirmBubble(oResponseObject.WarningMessage, oConfirmAction, oRejectionAction);
					}
					// No warning then reload the interface.
					else {

						var ViewIds = oResponseObject.ViewIds;

						// If there hasn't been any size change, none of the approvals have successfully had the status applied.
						if (ViewIds.length === g_oMultiView.g_oViews.length) {
							if (iStatus == eApproverStatus.Rejected) {
								g_oGreenlightMessages.displayWarningBubble(c_szMissingAnnotationsWarning)
							}
							else {
								g_oGreenlightMessages.displayWarningBubble(c_szIncompleteChecklistWarning);
							}
						}
						// If the response was the remaining view Ids which are pending..
						else if (ViewIds.length > 0) {

							// Filter out any view which is not in the list as they're now completed.
							var TempViews = [];
							for (var i = 0; i < g_oMultiView.g_oViews.length; i++) {
								if (ViewIds.indexOf(g_oMultiView.g_oViews[i].ViewId) !== -1) {
									TempViews.push(g_oMultiView.g_oViews[i]);
								}
							}

							g_oMultiView.g_oViews = TempViews;

							// Re-render & update using the first view.
							g_oMultiView.g_iIndex = 0;
							g_oMultiView.Load();

							// Let the user know what's happened.
							if (iStatus == eApproverStatus.Rejected) {
								g_oGreenlightMessages.displayWarningBubble(c_szMissingAnnotationsWarning)
							}
							else {
								g_oGreenlightMessages.displayWarningBubble(c_szIncompleteChecklistWarning);
							}
						}
						else {
							// Set the flag to not skip as we're likely closing down the viewer.
							g_oMultiView.bMoveAfterStatusSet = false;
							// Update current view.
							setApproverStatus_CH(iStatus);
						}
					}
				}
				else {
					g_oGreenlightMessages.displayErrorBubble(displayErrorContentHTML(oResponse.Errors));
				}
			}

		}).always(function () {
			$("#btnBatchReject").removeClass("selectedButton");
			$("#btnBatchApprove").removeClass("selectedButton");
		});

	}

	/**
	 * After setting the approvers status we need to refresh the page.
	 * @param {int} status - Id of the status that has been set.
	 */
	function setApproverStatus_CH(status) {
		// Update the user approval users details.
		var oApprovalUser = getApprovalUser();
		oApprovalUser.approverStatus = status;
		oApprovalUser.approverStatusDateTime = Date.now();
		// Update the approval details.
		approval.approvalStatus = status;
		approval.approvalStatusDateTime = Date.now();

		g_oGreenlightMessages.displaySuccessBubble(constants.c_szSucessfullySavedApproverStatus);

		// Remove the selected highlight from the approval icons.
		$("#tblApprovalTools .selectedButton").removeClass("selectedButton");

		var windowParent = parent;

		// Re-render the viewer.
		renderApprovalIcons();
		renderApproverInfoList();
		renderApprovalBar();
		renderChecklist();

		g_oEvents.publish("approverStatusSet", g_oViewer.g_oArtworkViewer, g_oViewer.g_oAnnotationViewer);

		setTimeout(function () {
			windowParent.postMessage("setUserStatus:" + status, "*");
		}, 1000);
	}

	/**
	 * Sets a constant string against the object.
	 * @param {string} constantName - The name of the constant to be added to the object.
	 * @param {string} value - The value of the constant.
	 */
	function setConstant(constantName, value) {

		if (!constants.hasOwnProperty.call(constants, constantName)) {
			constants[constantName] = value;
		}
	}

	/**
	 * Sets the read only value.
	 * @param {bool} readOnly - The new readOnlyValue.
	 */
	function setReadOnly(readOnly) {
		g_oViewer.g_bReadOnly = readOnly;
	}

	/**
	 * Hides the approval information when required.
	 */
	function hideApprovalInformation() {
		if (!g_oViewer.g_bReadOnly && g_oApproval.g_bApprovalInformationHidden) {
			$('#tblApprovalTools').html("");
		}
		$('#divApprovalMembers').html("");
		$('#tblInfoTools').html("");
		if (g_oApproval.g_bApprovalInformationHidden) {
			$("#divViewMenu").hide();
		}
	}

	/**
	* This is the class interface.
	* These are the public functions that can be seen from g_oApproval.
	*/
	return {
		ReadOnly: isReadOnly,
		UserIsSheriffInApproval: userIsSheriffInApproval,
		RegisterEvents: registerEvents,
		SetApproval: setApproval,
		SetApproverStatus: setApproverStatus,
		SetBatchApproverStatus: setBatchApproverStatus,
		SetConstant: setConstant,
		SetReadOnly: setReadOnly,
		CreateAnnotationsReport: createAnnotationsReport,
		DocChangeEvent: docChangeEvent,
		HideApprovalInformation: hideApprovalInformation,
		RenderApproverDisplayName: renderApproverDisplayName,
		ApprovalConditionsMet: approvalConditionsMet,
		RejectionConditionsMet: rejectionConditionsMet,
		UpdateApprovalButtonStates: updateApprovalButtonStates
	};
})();